import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CardNav from './CardNav';
import BackgroundDots from './BackgroundDots';
import DesktopLogin from './DesktopLogin';
import './LoopinoDesktopGuidelines.css';
import './DesktopApp.css';
import desktopNavSections from './navItems';
import { useAuthContext } from '../../context/AuthContext';
import DesktopPostReviewPage from './DesktopPostReviewPage';
import DesktopCalendarPage from './DesktopCalendar';
import DesktopBatteryPage from './DesktopBatteryPage';
import DesktopCompanyProfilePage from './DesktopCompanyProfile';
import DesktopProfileSettingsPage from './DesktopProfileSettings';
import DesktopImagePromptEditorPage from './DesktopImagePromptEditorPage';
import DesktopAIGeneratorPage from './DesktopAIGeneratorPage';
import DesktopStrategyPage from './DesktopStrategyPage';
import DesktopAIStoragePage from './DesktopAIStorage';
import DesktopImageGalleryPage from './DesktopImageGallery/DesktopImageGalleryPage';
import DesktopNewStrategyPage from './DesktopNewStrategyPage';
import DesktopSubscriptionPlansPage from './DesktopSubscriptionPlans';
import DesktopSplashScreen from './DesktopSplashScreen';
import DesktopStrategizerPage from './DesktopStrategizerPage';
import { LanguageProvider } from '../Mobile/contexts/LanguageContext';
import { registerDesktopTranslations } from './i18n/registerDesktopTranslations';
import { useCapabilities } from '../../capabilities/CapabilityContext';
import { trackPageView } from '../../utils/analytics';
import useCompany from '../../hooks/useCompany';
import { Skeleton } from '../effects';
import { hasCompanyDna } from '../../utils/companyDna';

registerDesktopTranslations();

const navItems = desktopNavSections;
const HOME_PATH = '/desktop/posts';

const resolveCapabilityHrefForView = (view, search = '') => {
  switch (view) {
    case 'ai-generator':
      return '/desktop/create-post';
    case 'ai-storage':
      return '/desktop/ai-storage';
    case 'subscription':
      return '/desktop/subscription';
    case 'battery':
      return '/desktop/battery';
    case 'strategizer': {
      const params = new URLSearchParams(search || '');
      if (params.get('view') === 'dna') {
        return `/strategizer${search || ''}`;
      }
      return null;
    }
    default:
      return null;
  }
};

const DesktopApp = () => {
  const { isAuthenticated } = useAuthContext();
  const { getRouteStateForHref, isAccessible, isReady: isCapabilityReady } = useCapabilities();
  const { currentCompany, loading: companyLoading } = useCompany();
  const resolveViewFromPath = useCallback((pathname) => {
    if (!pathname) return 'posts';
    if (pathname === '/strategizer' || pathname.startsWith('/strategizer/')) return 'strategizer';
    if (pathname.includes('/desktop/new-campaign') || pathname.includes('/desktop/new-strategy')) return 'new-strategy';
    if (pathname.includes('/desktop/profile/dna')) return 'strategizer';
    if (pathname.includes('/desktop/profile/settings')) return 'profile-settings';
    if (pathname.includes('/desktop/calendar')) return 'calendar';
    if (pathname.includes('/desktop/subscription')) return 'subscription';
    if (pathname.includes('/desktop/strategy')) return 'strategy';
    if (pathname.includes('/desktop/battery')) return 'battery';
    if (pathname.includes('/desktop/image-gallery')) return 'image-gallery';
    if (pathname.includes('/desktop/create-post') || pathname.includes('/desktop/ai-generator')) return 'ai-generator';
    if (pathname.includes('/desktop/image-generator')) return 'image-generator';
    if (pathname.includes('/desktop/ai-storage')) return 'ai-storage';
    if (pathname.includes('/desktop/profile') || pathname.includes('/desktop/settings')) return 'profile';
    return 'posts';
  }, []);

  const [currentView, setCurrentView] = useState(() => {
    if (typeof window === 'undefined') return 'posts';
    return resolveViewFromPath(window.location.pathname);
  });
  const [currentSearch, setCurrentSearch] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.location.search || '';
  });
  const [routeState, setRouteState] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.history.state || null;
  });
  const [pendingNav, setPendingNav] = useState(null);
  const [companyLoadComplete, setCompanyLoadComplete] = useState(false);
  const [hasSplashMinimumElapsed, setHasSplashMinimumElapsed] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);
  const hasAnnouncedReadyRef = useRef(false);
  const appGateRef = useRef(false);
  const companyLoadingRef = useRef(companyLoading);

  const resolveCompanyId = useCallback((company) => {
    if (!company) return '';
    return (
      company.id ||
      company._id ||
      company.company_id ||
      company.company?.id ||
      company.company?._id ||
      company.company?.company_id ||
      ''
    );
  }, []);

  const buildStrategizerDnaPath = useCallback(({ companyId, dnaGate } = {}) => {
    const params = new URLSearchParams();
    params.set('view', 'dna');
    if (companyId) {
      params.set('companyId', companyId);
    }
    if (dnaGate) {
      params.set('dnaGate', dnaGate);
    }
    return `/strategizer?${params.toString()}`;
  }, []);

  const handleDesktopLoginSuccess = useCallback(() => {
    console.log('[DesktopApp] Desktop login completed - rendering workspace view');
  }, []);

  const handleAppReady = useCallback(() => {
    if (hasAnnouncedReadyRef.current) return;
    hasAnnouncedReadyRef.current = true;
    setIsAppReady(true);
  }, []);

  const getCampaignIdFromSearch = useCallback((search) => {
    try {
      const params = new URLSearchParams(search || '');
      return params.get('campaignId') || params.get('targetCampaignId') || null;
    } catch (error) {
      console.error('[DesktopApp] Unable to parse campaignId from search', error);
      return null;
    }
  }, []);

  const requiresDnaGate = useCallback((view) => view === 'strategy' || view === 'new-strategy', []);

  const applyNavigation = useCallback((href, nextState = null) => {
    if (typeof window !== 'undefined' && href) {
      const url = new URL(href, window.location.origin);
      window.history.pushState(nextState, '', href);
      setCurrentSearch(url.search);
      setCurrentView(resolveViewFromPath(url.pathname));
      setRouteState(nextState);
      return;
    }
    setCurrentView(resolveViewFromPath(href));
    setCurrentSearch('');
    setRouteState(nextState);
  }, [resolveViewFromPath]);

  const handleNavigate = useCallback((href, options = {}) => {
    const nextState = options.state || null;
    if (!href) return;

    let normalizedHref = href;
    if (href.includes('/desktop/profile/dna')) {
      let dnaGate = null;
      try {
        const url = new URL(href, window.location.origin);
        dnaGate = new URLSearchParams(url.search || '').get('dnaGate');
      } catch (error) {
        dnaGate = null;
      }
      normalizedHref = buildStrategizerDnaPath({
        companyId: resolveCompanyId(currentCompany),
        dnaGate
      });
    }
    if (normalizedHref.includes('/desktop/ai-generator')) {
      normalizedHref = normalizedHref.replace('/desktop/ai-generator', '/desktop/create-post');
    }

    let nextPathname = normalizedHref;
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(normalizedHref, window.location.origin);
        nextPathname = url.pathname;
      } catch (error) {
        nextPathname = normalizedHref;
      }
    }

    const capabilityState = getRouteStateForHref(normalizedHref);
    if (capabilityState.managed && !isAccessible(capabilityState)) {
      applyNavigation(HOME_PATH, null);
      return;
    }

    const nextView = resolveViewFromPath(nextPathname);
    const isBootstrappingCompany = !companyLoadComplete;

    if (isAuthenticated && requiresDnaGate(nextView)) {
      if (isBootstrappingCompany) {
        setPendingNav({ href: normalizedHref, state: nextState });
        return;
      }
      if (currentCompany && !hasCompanyDna(currentCompany)) {
        applyNavigation(buildStrategizerDnaPath({
          companyId: resolveCompanyId(currentCompany),
          dnaGate: 'strategy'
        }), null);
        return;
      }
    }

    applyNavigation(normalizedHref, nextState);
  }, [
    applyNavigation,
    buildStrategizerDnaPath,
    companyLoadComplete,
    currentCompany,
    getRouteStateForHref,
    isAccessible,
    isAuthenticated,
    requiresDnaGate,
    resolveCompanyId,
    resolveViewFromPath
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pathname = window.location.pathname || '';
    if (!pathname.includes('/desktop/ai-generator')) return;
    const canonicalPath = pathname.replace('/desktop/ai-generator', '/desktop/create-post');
    const canonicalUrl = `${canonicalPath}${window.location.search || ''}${window.location.hash || ''}`;
    window.history.replaceState(window.history.state, '', canonicalUrl);
    setCurrentView(resolveViewFromPath(canonicalPath));
    setCurrentSearch(window.location.search || '');
    setRouteState(window.history.state || null);
  }, [resolveViewFromPath]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPopState = () => {
      setCurrentView(resolveViewFromPath(window.location.pathname));
      setCurrentSearch(window.location.search || '');
      setRouteState(window.history.state || null);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [resolveViewFromPath]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pathname = window.location.pathname || '';
    if (!pathname.includes('/desktop/profile/dna')) return;
    if (pathname.startsWith('/strategizer')) return;
    let dnaGate = null;
    try {
      const params = new URLSearchParams(window.location.search || '');
      dnaGate = params.get('dnaGate');
    } catch (error) {
      dnaGate = null;
    }
    applyNavigation(buildStrategizerDnaPath({
      companyId: resolveCompanyId(currentCompany),
      dnaGate
    }), null);
  }, [applyNavigation, buildStrategizerDnaPath, currentCompany, resolveCompanyId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const screenName = isAuthenticated ? `desktop:${currentView}` : 'desktop:login';
    const pageTitle = isAuthenticated
      ? `Loopino Desktop - ${currentView}`
      : 'Loopino Desktop - Login';

    trackPageView({
      screen_name: screenName,
      page_title: pageTitle
    });
  }, [currentSearch, currentView, isAuthenticated]);

  useEffect(() => {
    const timer = window.setTimeout(() => setHasSplashMinimumElapsed(true), 600);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const wasLoading = companyLoadingRef.current;
    if ((wasLoading && !companyLoading) || (currentCompany && !companyLoading)) {
      setCompanyLoadComplete(true);
    }
    companyLoadingRef.current = companyLoading;
  }, [companyLoading, currentCompany]);

  useEffect(() => {
    if (!isAuthenticated) {
      handleAppReady();
    }
  }, [handleAppReady, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && currentView !== 'posts') {
      handleAppReady();
    }
  }, [currentView, handleAppReady, isAuthenticated]);

  useEffect(() => {
    if (!pendingNav) return;
    if (!isAuthenticated) {
      applyNavigation(pendingNav.href, pendingNav.state);
      setPendingNav(null);
      return;
    }

    let nextPathname = pendingNav.href;
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(pendingNav.href, window.location.origin);
        nextPathname = url.pathname;
      } catch (error) {
        nextPathname = pendingNav.href;
      }
    }

    const nextView = resolveViewFromPath(nextPathname);
    if (!requiresDnaGate(nextView)) {
      applyNavigation(pendingNav.href, pendingNav.state);
      setPendingNav(null);
      return;
    }

    const isBootstrappingCompany = !companyLoadComplete;
    if (isBootstrappingCompany) return;

    if (currentCompany && !hasCompanyDna(currentCompany)) {
      applyNavigation(buildStrategizerDnaPath({
        companyId: resolveCompanyId(currentCompany),
        dnaGate: 'strategy'
      }), null);
    } else {
      applyNavigation(pendingNav.href, pendingNav.state);
    }
    setPendingNav(null);
  }, [
    applyNavigation,
    buildStrategizerDnaPath,
    companyLoadComplete,
    currentCompany,
    isAuthenticated,
    pendingNav,
    requiresDnaGate,
    resolveCompanyId,
    resolveViewFromPath
  ]);

  useEffect(() => {
    if (!requiresDnaGate(currentView)) {
      appGateRef.current = false;
      return;
    }
    if (!isAuthenticated) return;
    if (!companyLoadComplete) return;
    if (!currentCompany) return;
    if (hasCompanyDna(currentCompany)) return;
    if (appGateRef.current) return;

    appGateRef.current = true;
    applyNavigation(buildStrategizerDnaPath({
      companyId: resolveCompanyId(currentCompany),
      dnaGate: 'strategy'
    }), null);
  }, [
    applyNavigation,
    buildStrategizerDnaPath,
    companyLoadComplete,
    currentCompany,
    currentView,
    isAuthenticated,
    requiresDnaGate,
    resolveCompanyId
  ]);

  const currentCapabilityHref = useMemo(
    () => resolveCapabilityHrefForView(currentView, currentSearch),
    [currentSearch, currentView]
  );
  const currentRouteCapability = useMemo(
    () => (currentCapabilityHref ? getRouteStateForHref(currentCapabilityHref) : { managed: false, state: 'unmanaged' }),
    [currentCapabilityHref, getRouteStateForHref]
  );

  useEffect(() => {
    if (!isAuthenticated || !isCapabilityReady) {
      return;
    }
    if (!currentRouteCapability.managed || isAccessible(currentRouteCapability)) {
      return;
    }
    applyNavigation(HOME_PATH, null);
  }, [applyNavigation, currentRouteCapability, isAccessible, isAuthenticated, isCapabilityReady]);

  const renderedView = useMemo(() => {
    if (currentView === 'calendar') {
      return <DesktopCalendarPage queryString={currentSearch} onNavigate={handleNavigate} />;
    }
    if (currentView === 'battery') {
      return <DesktopBatteryPage onNavigate={handleNavigate} />;
    }
    if (currentView === 'profile-settings') {
      return <DesktopProfileSettingsPage onNavigate={handleNavigate} />;
    }
    if (currentView === 'profile') {
      return <DesktopCompanyProfilePage onNavigate={handleNavigate} />;
    }
    if (currentView === 'image-generator') {
      return (
        <DesktopImagePromptEditorPage
          onNavigate={handleNavigate}
          queryString={currentSearch}
          routeState={routeState}
        />
      );
    }
    if (currentView === 'ai-generator') {
      return (
        <DesktopAIGeneratorPage
          queryString={currentSearch}
          routeState={routeState}
          onNavigate={handleNavigate}
        />
      );
    }
    if (currentView === 'strategy') {
      const targetCampaignId = getCampaignIdFromSearch(currentSearch) ||
        routeState?.targetCampaignId ||
        routeState?.campaignId ||
        null;
      return (
        <DesktopStrategyPage
          onNavigate={handleNavigate}
          targetCampaignId={targetCampaignId}
          queryString={currentSearch}
        />
      );
    }
    if (currentView === 'ai-storage') {
      return <DesktopAIStoragePage />;
    }
    if (currentView === 'new-strategy') {
      return <DesktopNewStrategyPage onNavigate={handleNavigate} />;
    }
    if (currentView === 'subscription') {
      return <DesktopSubscriptionPlansPage />;
    }
    if (currentView === 'image-gallery') {
      return (
        <DesktopImageGalleryPage
          mode="standalone"
          viewerOrigin="desktop-nav"
        />
      );
    }
    if (currentView === 'strategizer') {
      return <DesktopStrategizerPage queryString={currentSearch} />;
    }
    return <DesktopPostReviewPage onNavigate={handleNavigate} onReady={handleAppReady} />;
  }, [currentSearch, currentView, handleAppReady, handleNavigate, routeState]);

  const isStrategizerView = isAuthenticated && currentView === 'strategizer';
  const isLoginView = !isAuthenticated;
  const shouldShowSplash = !hasSplashMinimumElapsed || !isAppReady;
  const shouldHoldRoute = isAuthenticated && requiresDnaGate(currentView) &&
    (!companyLoadComplete || (currentCompany && !hasCompanyDna(currentCompany)));
  const shouldHoldCapabilityRoute = isAuthenticated
    && Boolean(currentCapabilityHref)
    && !isCapabilityReady;
  const pendingNavView = useMemo(() => {
    if (!pendingNav) return null;
    try {
      const url = typeof window !== 'undefined'
        ? new URL(pendingNav.href, window.location.origin)
        : { pathname: pendingNav.href };
      return resolveViewFromPath(url.pathname);
    } catch (error) {
      return resolveViewFromPath(pendingNav.href);
    }
  }, [pendingNav, resolveViewFromPath]);

  const shouldShowRouteHold = shouldHoldRoute
    || shouldHoldCapabilityRoute
    || (Boolean(pendingNav) && pendingNavView !== currentView);

  useEffect(() => {
    if (!pendingNav) return;
    if (pendingNavView !== currentView) return;
    setPendingNav(null);
  }, [currentView, pendingNav, pendingNavView]);

  return (
    <LanguageProvider>
      <div
        className={`desktop-app-shell${isStrategizerView ? ' desktop-app-shell--strategizer' : ''}${
          isLoginView ? ' desktop-app-shell--login' : ''
        }`}
      >
        <BackgroundDots />
        <div className={`desktop-app-content${isStrategizerView ? ' desktop-app-content--strategizer' : ''}`}>
          {isAuthenticated && (
            <CardNav items={navItems} className="desktop-app-card-nav" onNavigate={handleNavigate} />
          )}
          {isAuthenticated ? (
            <div className={`desktop-app-home${isStrategizerView ? ' desktop-app-home--strategizer' : ''}`}>
              {!shouldHoldRoute && renderedView}
            </div>
          ) : (
            <DesktopLogin onLoginSuccess={handleDesktopLoginSuccess} />
          )}
        </div>
        {shouldShowRouteHold && (
          <div className="desktop-app-route-hold" role="status" aria-live="polite" aria-atomic="true">
            <div className="desktop-app-route-card">
              <Skeleton width="120px" height={10} shimmer />
              <Skeleton width="280px" height={28} shimmer style={{ marginTop: 12 }} />
              <Skeleton width="420px" height={12} shimmer style={{ marginTop: 10 }} />
            </div>
          </div>
        )}
        {shouldShowSplash && <DesktopSplashScreen />}
      </div>
    </LanguageProvider>
  );
};

export default DesktopApp;
